// fork of https://github.com/pawelgrzybek/siema/blob/master/src/siema.js
import noop from 'lodash/noop';
import once from 'lodash/once';
import over from 'lodash/over';
import forEach from 'lodash/forEach';
import on from '../../lib/dom/on';
import isTouch from '../../lib/dom/is-touch';
import setStyle from '../../lib/dom/set-style';
import iterateElements from '../../lib/dom/iterate';
import elementSize from '../../lib/dom/element-size';
import { isRtl } from '../../lib/document-dir-utils';



export default class SmGallery {
  /**
   * @param {Node|NodeList} frameElement
   * @param {Object} options
   */
  constructor (frameElement, { duration = 200, easing = 'ease-out', threshold = 10, onInit = noop, onChange = noop, onClick = noop, beforeRefresh = noop, onBeforeChange = noop, disableDrag = false } = {}) {
    this._duration = duration;
    this._disableDrag = disableDrag;
    this._easing = easing;
    this._threshold = threshold;
    this._onChange = onChange;
    this._beforeRefresh = beforeRefresh;
    this._onClick = onClick;
    this._onBeforeChange = onBeforeChange;
    this.frame = frameElement && frameElement.length ? frameElement[0] : frameElement;
    this.slider = frameElement.parentNode;
    this.currentSlide = 0;
    this._disabled = false;
    this._isRtl = isRtl();

    this.refresh = this.refresh.bind(this);

    this.refresh();

    this.destroy = once(over([
      isTouch ? this._bindTouchEvents() : this._bindMouseEvents()
    ]));

    onInit.call(this);
  }

  enable () {
    this._disabled = false;
  }

  disable ({ disableClick = true } = {}) {
    this._disabled = true;
    this._disabledClick = disableClick;
  }

  refresh () {
    this._beforeRefresh();
    this._clearDrag();

    this._slidePositions = [];
    this._slideWidths = [];
    this._frameWidth = 0;
    this._applyingWidth = 0;

    this._width = this.slider.offsetWidth;

    iterateElements(this.frame.children, (element) => {
      const { width } = elementSize(element);
      const widthByDirection = (this._isRtl ? -1 : 1) * this._frameWidth;

      this._slidePositions.push(widthByDirection);
      this._slideWidths.push(width);
      this._frameWidth += width;

      this._applyingWidth += Math.min(width, this._width);
    });

    this.frame.style.width = `${this._frameWidth}px`;
    this._maxPosition = this._frameWidth - this._width;

    this._minSlide = 0;
    this._maxSlide = this._calcMaxSlide();

    this._slidePosWidths = [];
    this._slideNegWidths = [];

    forEach(this._slideWidths, (_1, index) => {
      this._slidePosWidths[index] = this._slideWidths.slice(index);
      this._slideNegWidths[index] = [];

      let i = index;

      while (i-- > 0) {
        this._slideNegWidths[index].push(this._slideWidths[i]);
      }

      if (this._slidePosWidths[index][0]) {
        this._slidePosWidths[index][0] = this._slidePosWidths[index][0] * (2 / 3);
      }

      if (this._slideNegWidths[index][0]) {
        this._slideNegWidths[index][0] = this._slideNegWidths[index][0] * (2 / 3);
      }
    });

    this._refreshPosition();
  }

  _calcMaxSlide () {
    let width = 0;
    let slide = 0;

    for (; slide < this._slideWidths.length; slide++) {
      width += Math.min(this._width, this._slideWidths[slide]);

      if (width >= this._applyingWidth) {
        return slide;
      }
    }

    return slide;
  }

  goTo (slideIndex, instant) {
    if (this._maxPosition <= 0) {
      this._refreshPosition();
      return;
    }

    const nextSlide = Math.min(Math.max(slideIndex, this._minSlide), this._maxSlide);

    if (nextSlide !== this.currentSlide) {
      this._slideTo(nextSlide, instant);
    } else {
      this._refreshPosition();
    }
  }

  _slideTo (slideIndex, instant) {
    const position = this._calcSlidePosition(slideIndex);

    if (slideIndex !== this.currentSlide) {
      this._onBeforeChange(slideIndex);
    }

    this._setPosition(position, instant);

    if (slideIndex !== this.currentSlide) {
      this.currentSlide = slideIndex;
      this._onChange(slideIndex);
    }
  }

  _refreshPosition () {
    this._slideTo(this.currentSlide);
  }

  _setPosition (position, instant = false) {
    if (!this._disableDrag) {
      if (instant) {
        this._setTransition(0);
      }
      setStyle(this.frame, 'transform', `translate3d(${position * -1}px, 0, 0)`);
      if (instant) {
        requestAnimationFrame(() => {
          this._setTransition(this._duration);
        });
      }
    }
  }

  _setTransition (duration) {
    setStyle(this.frame, 'transition', `all ${duration}ms ${this._easing}`);
  }

  /**
   * @param {Number} slideIndex
   * @returns {Number}
   */
  _calcSlidePosition (slideIndex) {
    if (this._maxPosition <= 0) {
      return 0;
    }

    const offset = Math.min(this._maxPosition, Math.abs(this._slidePositions[slideIndex]));

    return this._isRtl ? -offset : offset;
  }

  _calcSlidesCount (distance, positiveMovement) { // eslint-disable-line
    const widths = (positiveMovement ? this._slidePosWidths : this._slideNegWidths)[this.currentSlide];
    const len = widths.length;

    for (let i = 0; i < len; i++) {
      distance -= widths[i];

      if (distance < 0) {
        return i + 1;
      }
    }

    return len;
  }

  _clearDrag () {
    this._dragEndX = 0;
    this._dragStartX = 0;
    this._dragStartY = 0;
    this._dragMoved = false;
    this._dragStarted = false;
    this._draggingHorizontally = null;
  }

  _updateAfterDrag () {
    this._setTransition(this._duration);

    const movement = this._dragStartX - this._dragEndX;
    const distance = Math.abs(movement);

    if (distance > this._threshold) {
      const positiveMovement = this._isRtl ? movement < 0 : movement > 0;
      const slides = (positiveMovement ? 1 : -1) * this._calcSlidesCount(distance, positiveMovement);

      this.goTo(this.currentSlide + slides);
    } else {
      this._refreshPosition();
    }

    this._clearDrag();
  }

  _dragTheFrame () {
    this._setTransition(0);
    this._setPosition(this._calcSlidePosition(this.currentSlide) + this._dragStartX - this._dragEndX);
  }

  _onDragStart (ev, pointer) {
    this._clearDrag();
    ev.stopPropagation();
    this._dragStarted = true;
    this._dragStartX = pointer.pageX;
    this._dragStartY = pointer.pageY;
  }

  _onDragging (ev, pointer) {
    if (!this._dragStarted) {
      return;
    }

    this._dragEndX = pointer.pageX;
    this._dragMoved = this._dragMoved || Math.abs(this._dragStartX - this._dragEndX) >= this._threshold;

    if (this._disabled) {
      return;
    }

    if (this._draggingHorizontally === null) {
      this._draggingHorizontally = Math.abs(this._dragStartY - pointer.pageY) <= Math.abs(this._dragStartX - pointer.pageX);
    }

    if (this._draggingHorizontally) {
      if (ev.cancelable) {
        ev.stopPropagation();
        ev.preventDefault();
      }
      this._dragTheFrame();
    }
  }

  _onDrop (ev) {
    if (!this._dragStarted) {
      this._clearDrag();
      return;
    }

    if (this._disabled) {
      if (!this._disabledClick && !this._dragMoved) {
        this._onClick(ev);
      }

      this._clearDrag();
      return;
    }

    if (this._draggingHorizontally === false) {
      this._clearDrag();
      this._refreshPosition();
      return;
    }

    if (this._draggingHorizontally) {
      if (ev.cancelable) {
        ev.stopPropagation();
        ev.preventDefault();
      }
      this._updateAfterDrag();
      return;
    }

    if (!this._dragMoved) {
      this._clearDrag();
      this._onClick(ev);
    }
  }

  _onLeaveDraggingZone (pointer) {
    if (!this._dragStarted) {
      return;
    }

    if (this._disabled) {
      this._clearDrag();
      return;
    }

    this._dragEndX = pointer.pageX;
    this._updateAfterDrag();
  }

  _bindMouseEvents () {
    return over([
      on(this.slider, 'mousedown', (ev) => {
        this._onDragStart(ev, ev);
      }),
      on(this.slider, 'mouseup', (ev) => {
        this._onDrop(ev);
      }),
      on(this.slider, 'mouseleave', (ev) => {
        this._onLeaveDraggingZone(ev);
      }),
      on(this.slider, 'mousemove', (ev) => {
        this._onDragging(ev, ev);
      })
    ]);
  }

  _bindTouchEvents () {
    return over([
      on(this.slider, 'touchstart', (ev) => {
        this._onDragStart(ev, ev.touches[0]);
      }, { passive: true }),
      on(this.slider, 'touchend', (ev) => {
        this._onDrop(ev);
      }),
      on(this.slider, 'touchmove', (ev) => {
        this._onDragging(ev, ev.touches[0]);
      }, { passive: true })
    ]);
  }
}



// WEBPACK FOOTER //
// ./src/vendor/siema/index.js